import React, { useState, useEffect } from 'react';
import { FaTimes, FaTag, FaClock } from 'react-icons/fa';

/**
 * StickyAdBanner - Persistent banner that sticks to top/bottom of page
 */
const StickyAdBanner = ({ 
  ad, 
  position = 'top', // 'top' or 'bottom'
  isVisible = true, 
  onClose,
  onAction,
  autoHide = false,
  hideAfter = 10000, // Hide after 10 seconds if autoHide is true
  showCloseButton = true
}) => {
  const [show, setShow] = useState(isVisible);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    if (autoHide && isVisible) {
      const timer = setTimeout(() => {
        handleClose();
      }, hideAfter);
      return () => clearTimeout(timer);
    }
  }, [autoHide, hideAfter, isVisible]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setShow(false);
      onClose?.();
    }, 300);
  };

  const handleAction = () => {
    if (onAction) {
      onAction(ad);
    }
    
    if (ad.link && ad.link !== '#') {
      if (ad.openInNewTab) {
        window.open(ad.link, '_blank');
      } else {
        window.location.href = ad.link;
      }
    }
  };

  if (!show || !ad) return null;

  return (
    <div 
      className={`fixed left-0 right-0 z-40 transition-all duration-300 ${
        position === 'top' ? 'top-0' : 'bottom-0'
      } ${isClosing ? 'transform -translate-y-full opacity-0' : ''}`}
    >
      <div className={`relative bg-gradient-to-r ${ad.backgroundColor || 'from-blue-600 to-purple-600'} text-white shadow-lg`}>
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            {/* Left Section - Icon/Badge */}
            <div className="flex items-center space-x-3 flex-shrink-0">
              {ad.icon && (
                <div className="flex items-center justify-center w-8 h-8 bg-white bg-opacity-20 rounded-full">
                  {ad.icon === 'tag' && <FaTag className="w-4 h-4" />}
                  {ad.icon === 'clock' && <FaClock className="w-4 h-4" />}
                </div>
              )}
              
              {ad.badge && (
                <span className="bg-yellow-400 text-gray-900 px-2 py-1 rounded-full text-xs font-bold">
                  {ad.badge}
                </span>
              )}
            </div>

            {/* Center Section - Content */}
            <div 
              className="flex-1 text-center cursor-pointer hover:opacity-90 transition-opacity"
              onClick={handleAction}
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-center md:space-x-4">
                {/* Title */}
                {ad.title && (
                  <span className="font-semibold text-sm md:text-base">
                    {ad.title}
                  </span>
                )}
                
                {/* Description */}
                {ad.description && (
                  <span className="text-xs md:text-sm opacity-90">
                    {ad.description}
                  </span>
                )}

                {/* CTA */}
                {ad.ctaText && (
                  <span className="inline-block mt-1 md:mt-0 bg-white bg-opacity-20 hover:bg-opacity-30 px-3 py-1 rounded-full text-xs font-medium transition-all duration-200">
                    {ad.ctaText} →
                  </span>
                )}
              </div>
            </div>

            {/* Right Section - Close Button */}
            <div className="flex items-center space-x-2 flex-shrink-0">
              {/* Timer for limited offers */}
              {ad.countdown && (
                <div className="hidden md:block text-xs bg-white bg-opacity-20 px-2 py-1 rounded">
                  <MiniCountdown targetDate={ad.countdown} />
                </div>
              )}

              {showCloseButton && (
                <button
                  onClick={handleClose}
                  className="p-1 hover:bg-white hover:bg-opacity-20 rounded-full transition-all duration-200"
                  aria-label="Close banner"
                >
                  <FaTimes className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Progress bar for auto-hide */}
        {autoHide && (
          <div className="absolute bottom-0 left-0 right-0 h-1 bg-white bg-opacity-20">
            <div 
              className="h-full bg-white bg-opacity-50 transition-all duration-100"
              style={{ 
                animation: `shrink ${hideAfter}ms linear forwards`,
                transformOrigin: 'left'
              }}
            />
          </div>
        )}
      </div>

      <style jsx>{`
        @keyframes shrink {
          from {
            width: 100%;
          }
          to {
            width: 0%;
          }
        }
      `}</style>
    </div>
  );
};

/**
 * Mini Countdown Timer for sticky banner
 */
const MiniCountdown = ({ targetDate }) => {
  const [timeLeft, setTimeLeft] = useState('');

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date().getTime();
      const target = new Date(targetDate).getTime();
      const difference = target - now;

      if (difference > 0) {
        const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const seconds = Math.floor((difference % (1000 * 60)) / 1000);
        
        if (hours > 0) {
          setTimeLeft(`${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`);
        } else {
          setTimeLeft(`${minutes}:${String(seconds).padStart(2, '0')}`);
        }
      } else {
        setTimeLeft('Expired');
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [targetDate]);

  return <span>{timeLeft}</span>;
};

export default StickyAdBanner;